import { useEffect, useState } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  Button,
} from "@mui/material";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast.jsx";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Helmet } from "react-helmet-async";
const defaultStructure = {
  coreClassesPerWeek: 3,
  coreClassDescriptions: [""],
  optionalClass: {
    enabled: false,
    options: [""],
    description: "",
  },
};

const defaultBatch = {
  name: "",
  startDate: "",
  endDate: "",
  levelId: "",
  daysOfWeek: [],
  timeSlot: ["", ""], // [start, end]
  maxLearners: 0,
  structure: { ...defaultStructure },
};

const daysList = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
function AdminBatches() {
  // Fix: Define openDeleteConfirm
  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  // Fix: Define handleModalClose
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ ...defaultBatch, structure: { ...defaultStructure } });
  };

  // Fix: Define handleAddOrUpdate
  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.levelId) {
      showToast("Name and Level are required.", "warning");
      return;
    }
    try {
      const payload = {
        ...form,
        timeSlot:
          form.timeSlot[0] && form.timeSlot[1]
            ? `${form.timeSlot[0]}-${form.timeSlot[1]}`
            : "",
        maxLearners: Number(form.maxLearners),
      };
      if (editingId) {
        await apiWithRefresh.patch(`batches/${editingId}`, payload);
        showToast("Batch updated successfully!", "success");
      } else {
        await apiWithRefresh.post("batches", payload);
        showToast("Batch added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm({ ...defaultBatch, structure: { ...defaultStructure } });
      fetchBatches();
    } catch (err) {
      showToast(err.message, "error");
    }
  };
  useDarkMode();
  const [batches, setBatches] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add form state for batch form
  const [form, setForm] = useState({
    ...defaultBatch,
    structure: { ...defaultStructure },
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();

  // Fetch batches and levels
  const fetchBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiWithRefresh.get("batches");
      setBatches(Array.isArray(data) ? data : []);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const fetchLevels = async () => {
    try {
      const data = await apiWithRefresh.get("levels");
      setLevels(Array.isArray(data) ? data : []);
    } catch (err) {
      // ignore for now
    }
  };

  useEffect(() => {
    fetchBatches();
    fetchLevels();
  }, []);

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("structure.optionalClass.")) {
      const key = name.replace("structure.optionalClass.", "");
      setForm((f) => ({
        ...f,
        structure: {
          ...f.structure,
          optionalClass: {
            ...f.structure.optionalClass,
            [key]: type === "checkbox" ? checked : value,
          },
        },
      }));
    } else if (name.startsWith("structure.")) {
      const key = name.replace("structure.", "");
      setForm((f) => ({
        ...f,
        structure: {
          ...f.structure,
          [key]: type === "number" ? Number(value) : value,
        },
      }));
    } else if (name === "daysOfWeek") {
      const day = value;
      setForm((f) => ({
        ...f,
        daysOfWeek: checked
          ? [...f.daysOfWeek, day]
          : f.daysOfWeek.filter((d) => d !== day),
      }));
    } else if (name === "maxLearners") {
      setForm((f) => ({ ...f, maxLearners: Number(value) }));
    } else {
      setForm((f) => ({ ...f, [name]: value }));
    }
  };

  // Array handlers for coreClassDescriptions and options
  const handleArrayChange = (arrName, idx, value) => {
    setForm((f) => {
      if (arrName === "coreClassDescriptions") {
        const arr = [...f.structure.coreClassDescriptions];
        arr[idx] = value;
        return {
          ...f,
          structure: { ...f.structure, coreClassDescriptions: arr },
        };
      } else if (arrName === "options") {
        const arr = [...f.structure.optionalClass.options];
        arr[idx] = value;
        return {
          ...f,
          structure: {
            ...f.structure,
            optionalClass: { ...f.structure.optionalClass, options: arr },
          },
        };
      }
      return f;
    });
  };
  const handleAddArrayItem = (arrName) => {
    setForm((f) => {
      if (arrName === "coreClassDescriptions") {
        return {
          ...f,
          structure: {
            ...f.structure,
            coreClassDescriptions: [...f.structure.coreClassDescriptions, ""],
          },
        };
      } else if (arrName === "options") {
        return {
          ...f,
          structure: {
            ...f.structure,
            optionalClass: {
              ...f.structure.optionalClass,
              options: [...f.structure.optionalClass.options, ""],
            },
          },
        };
      }
      return f;
    });
  };
  const handleRemoveArrayItem = (arrName, idx) => {
    setForm((f) => {
      if (arrName === "coreClassDescriptions") {
        const arr = f.structure.coreClassDescriptions.filter(
          (_, i) => i !== idx,
        );
        return {
          ...f,
          structure: { ...f.structure, coreClassDescriptions: arr },
        };
      } else if (arrName === "options") {
        const arr = f.structure.optionalClass.options.filter(
          (_, i) => i !== idx,
        );
        return {
          ...f,
          structure: {
            ...f.structure,
            optionalClass: { ...f.structure.optionalClass, options: arr },
          },
        };
      }
      return f;
    });
  };

  // Modal controls
  const openAddModal = () => {
    setForm({ ...defaultBatch, structure: { ...defaultStructure } });
    setEditingId(null);
    setModalOpen(true);
  };
  const openEditModal = async (batch) => {
    try {
      // Fetch latest batch details from backend
      const detail = await apiWithRefresh.get(`batches/${batch.id}`);
      // Ensure levelId is always a string (the id)
      let levelId = detail.levelId;
      if (levelId && typeof levelId === "object") {
        levelId = levelId.id || "";
      }
      let timeSlot = ["", ""];
      if (Array.isArray(detail.timeSlot)) {
        timeSlot = detail.timeSlot;
      } else if (
        typeof detail.timeSlot === "string" &&
        detail.timeSlot.includes("-")
      ) {
        const [start, end] = detail.timeSlot.split("-").map((s) => s.trim());
        timeSlot = [start, end];
      }
      setForm({
        ...defaultBatch,
        ...detail,
        startDate: detail.startDate ? detail.startDate.split("T")[0] : "",
        endDate: detail.endDate ? detail.endDate.split("T")[0] : "",
        levelId,
        timeSlot,
        structure: {
          ...defaultStructure,
          ...detail.structure,
          optionalClass: {
            ...defaultStructure.optionalClass,
            ...(detail.structure?.optionalClass || {}),
          },
        },
      });
      setEditingId(batch.id);
      setModalOpen(true);
    } catch (err) {
      showToast(err.message || "Failed to load batch details", "error");
    }
  };
  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`batches/${deleteId}`);
      showToast("Batch deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchBatches();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // DataGrid columns

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Users</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Manage Batches
        </Typography>
        {loading && <Typography>Loading...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box sx={{ width: "100%", maxWidth: 1000 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddModal}
              sx={{ mb: 2 }}
            ></Button>
            <DataGrid
              autoHeight
              rows={batches.map((b) => ({
                ...b,
                id: b.id,
                LevelName:
                  b.levelId && typeof b.levelId === "object"
                    ? b.levelId.name || b.levelId.id || "-"
                    : typeof b.levelId === "string"
                      ? b.levelId
                      : "-",
                daysOfWeek: Array.isArray(b.daysOfWeek)
                  ? b.daysOfWeek.join(", ")
                  : typeof b.daysOfWeek === "string"
                    ? b.daysOfWeek
                    : "-",
                timeSlot: Array.isArray(b.timeSlot)
                  ? `${b.timeSlot[0] || ""}-${b.timeSlot[1] || ""}`
                  : typeof b.timeSlot === "string"
                    ? b.timeSlot
                    : "-",
              }))}
              columns={[
                { field: "name", headerName: "Name", flex: 1 },
                { field: "startDate", headerName: "Start Date", flex: 1,
                  valueFormatter: (params) => {
                    if (!params) return "-";
                    const date = new Date(params);
                    return isNaN(date.getTime()) ? "-" : date.toDateString();
                  }
                 },
                { field: "endDate", headerName: "End Date", flex: 1,
                  valueFormatter: (params) => {
                    if (!params) return "-";
                    const date = new Date(params);
                    return isNaN(date.getTime()) ? "-" : date.toDateString();
                  }
                 },
                { field: "daysOfWeek", headerName: "Days", flex: 1 },

                {
                  field: "actions",
                  headerName: "Actions",
                  sortable: false,
                  renderCell: (params) => (
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        color="primary"
                        onClick={() => openEditModal(params.row)}
                        aria-label={`Update batch ${params.row.name}`}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => openDeleteConfirm(params.row.id)}
                        aria-label={`Delete batch ${params.row.name}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  ),
                  flex: 1,
                },
              ]}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
        {/* Add/Edit Dialog */}
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingId ? "Edit Batch" : "Add New Batch"}
          </DialogTitle>
          <form onSubmit={handleAddOrUpdate}>
            <DialogContent>
              <TextField
                label="Batch Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
                inputProps={{ maxLength: 64 }}
              />
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={form.startDate || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  inputProps={{
                    min: new Date().toISOString().split('T')[0],
                  }}
                />
                <TextField
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={form.endDate || ""}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  required
                  inputProps={{
                    min: form.startDate ? form.startDate : new Date().toISOString().split('T')[0],
                  }}
                />
              </Stack>
              <Select
                label="Level"
                name="levelId"
                value={form.levelId}
                onChange={handleChange}
                fullWidth
                required
                sx={{ mb: 2 }}
                displayEmpty
              >
                <MenuItem value="">Select Level</MenuItem>
                {levels.map((level) => (
                  <MenuItem key={level.id} value={level.id}>
                    {level.name}
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Days of Week:
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                {daysList.map((day) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        name="daysOfWeek"
                        value={day}
                        checked={form.daysOfWeek.includes(day)}
                        onChange={handleChange}
                      />
                    }
                    label={day}
                  />
                ))}
              </Stack>
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                  label="Start Time"
                  type="time"
                  name="timeSlotStart"
                  value={form.timeSlot[0]}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      timeSlot: [e.target.value, f.timeSlot[1]],
                    }))
                  }
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  label="End Time"
                  type="time"
                  name="timeSlotEnd"
                  value={form.timeSlot[1]}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      timeSlot: [f.timeSlot[0], e.target.value],
                    }))
                  }
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <TextField
                label="Max Learners"
                name="maxLearners"
                type="number"
                value={form.maxLearners}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1, max: 999 }}
                sx={{ mb: 2 }}
                placeholder="e.g. 30"
              />
              <TextField
                label="Core Classes Per Week"
                name="structure.coreClassesPerWeek"
                type="number"
                value={form.structure.coreClassesPerWeek}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 1, max: 7 }}
                required
                sx={{ mb: 2 }}
              />
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Core Class Descriptions:
              </Typography>
              {form.structure.coreClassDescriptions.map((desc, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <TextField
                    label={`Description ${idx + 1}`}
                    value={desc}
                    onChange={(e) =>
                      handleArrayChange(
                        "coreClassDescriptions",
                        idx,
                        e.target.value,
                      )
                    }
                    fullWidth
                    sx={{ mr: 1 }}
                  />
                  <Button
                    color="error"
                    onClick={() =>
                      handleRemoveArrayItem("coreClassDescriptions", idx)
                    }
                    disabled={form.structure.coreClassDescriptions.length === 1}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button
                onClick={() => handleAddArrayItem("coreClassDescriptions")}
                sx={{ mt: 1 }}
              >
                Add Description
              </Button>
              <FormControlLabel
                control={
                  <Checkbox
                    name="structure.optionalClass.enabled"
                    checked={form.structure.optionalClass.enabled}
                    onChange={handleChange}
                  />
                }
                label="Enable Optional Class"
                sx={{ mt: 2 }}
              />
              {form.structure.optionalClass.enabled && (
                <>
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Optional Class Options:
                  </Typography>
                  {form.structure.optionalClass.options.map((opt, idx) => (
                    <Box
                      key={idx}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      <TextField
                        label={`Option ${idx + 1}`}
                        value={opt}
                        onChange={(e) =>
                          handleArrayChange("options", idx, e.target.value)
                        }
                        fullWidth
                        sx={{ mr: 1 }}
                      />
                      <Button
                        color="error"
                        onClick={() => handleRemoveArrayItem("options", idx)}
                        disabled={
                          form.structure.optionalClass.options.length === 1
                        }
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                  <Button
                    onClick={() => handleAddArrayItem("options")}
                    sx={{ mt: 1 }}
                  >
                    Add Option
                  </Button>
                  <TextField
                    label="Optional Class Description"
                    name="structure.optionalClass.description"
                    value={form.structure.optionalClass.description}
                    onChange={handleChange}
                    fullWidth
                    sx={{ mt: 2 }}
                    inputProps={{ maxLength: 128 }}
                    placeholder="Description"
                  />
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Delete Confirm Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this batch?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <GlobalToast toast={toast} />
      </Box>
    </>
  );
}
export default AdminBatches;
